/**
 * `#/pair` — where a display types the code a parent made in Family Studio (#582, E36).
 *
 * Until this screen, `POST /api/displays/claim` shipped with nothing that called it, so a code made
 * on the server could not be used from the app. The flow is two calls, in `api/displays.js`: claim
 * the code, then trade the display token it returns for a session. The token never reaches this
 * component.
 *
 * **The session replaces whoever was signed in on this browser.** The exchange regenerates the
 * session, so a parent pairing their own phone by mistake would be signed out of it. The screen
 * says so before anyone presses.
 *
 * **"Paired" is the SERVER's word, never this screen's** (#582 slice 5). A claim alone proves the
 * code was right; an exchange that answered proves a request was accepted. Only when
 * `GET /api/auth/verify` then names THIS browser as a shared display does the screen move on, to
 * `#/display` — the live board. A display the server still holds on the old borrowed-admin session
 * (before slice 6) is taken there too, and the board itself says it is not switched on yet. The
 * app's person-actor is never refreshed from a display session: `api/session.js` reads a display
 * as a device, never as the parent it once borrowed.
 *
 * **The token is not kept.** The session cookie lasts 30 days
 * (`server/auth/sessions/sessionMiddleware.js`). When it ends, the display is paired again with a
 * new code. Keeping the token in browser storage would let the display sign itself back in, and
 * would also leave a household credential where any script on the page can read it.
 *
 * Registered in `App.jsx` beside `/verify-email`, NOT in `ROUTES`, which is the inventory of the 18
 * recovered surfaces and is counted by three guards.
 */
import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '../api/client.js';
import { DisplaySignInError, pairThisDisplay } from '../api/displays.js';
import { fetchDisplaySession } from '../api/session.js';
import { useActor } from '../state/ActorContext.jsx';

const DISPLAY_BOARD_PATH = '/display';

/** The exchange answered, but the server could not be asked whether this is now a display. */
class DisplayUnconfirmedError extends Error {}

export { CLAIM_PATH, EXCHANGE_PATH } from '../api/displays.js';

export const PAIR_PATH = '/pair';

const FAULT = 'We could not pair this display just now. Check the code and try again in a moment.';

/** One message for a pairing that did not finish. */
export function pairMessage(error) {
  if (error instanceof DisplayUnconfirmedError) {
    return 'The code worked, but we could not confirm this display with the server. Reload this page '
      + 'in a moment to check again.';
  }
  if (error instanceof DisplaySignInError) {
    return 'The code worked, but this display could not sign in. Ask a parent to unpair it in '
      + 'Family Studio, make a new code, and pair this display again.';
  }
  if (error instanceof ApiError && error.status === 429) {
    return 'Too many tries from this display. Wait fifteen minutes, then try again.';
  }
  if (error instanceof ApiError && error.status === 401) {
    // The server sends ONE body for an unknown, expired or spent code (`CLAIM_REFUSED`). Show it
    // as sent, so this screen can never tell those three apart either.
    const said = [error.body?.error, error.body?.message]
      .filter((part) => typeof part === 'string' && part.trim())
      .map((part) => part.trim().replace(/\.?$/, '.'));
    return said.length ? said.join(' ') : FAULT;
  }
  return FAULT;
}

export default function PairRoute() {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [state, setState] = useState({ status: 'idle', message: null });
  const navigate = useNavigate();
  const { setActor } = useActor();
  // One press, one claim. A second claim of the same code would be refused by the server, and its
  // refusal would replace the success on screen.
  const inFlight = useRef(false);

  const submit = useCallback(async (event) => {
    event.preventDefault();
    if (inFlight.current || !code.trim()) return;
    inFlight.current = true;
    setState({ status: 'pairing', message: null });
    try {
      await pairThisDisplay(code.trim(), name);
      let answer;
      try {
        answer = await fetchDisplaySession();
      } catch (error) {
        throw new DisplayUnconfirmedError(error?.message);
      }
      // A person, or nobody: the exchange did not leave this browser holding a display session.
      if (answer.status !== 'device') throw new DisplaySignInError(null);
      navigate(DISPLAY_BOARD_PATH, { replace: true });
      setActor(null);
    } catch (error) {
      setState({ status: 'failed', message: pairMessage(error) });
    } finally {
      inFlight.current = false;
    }
  }, [code, name, navigate, setActor]);

  const pairing = state.status === 'pairing';
  return (
    <main style={{ padding: '2rem', maxWidth: '34rem' }} data-component="PairDisplay" data-state={state.status}>
      <h1>Pair this display</h1>
      <p>
        A parent makes a six-character code in Family Studio. Type it here. The code works once, for
        ten minutes.
      </p>
      <p>Pairing signs out anyone signed in on this browser.</p>
      <form onSubmit={submit}>
        <p>
          <label htmlFor="pair-code">Pairing code</label>
          <br />
          <input
            id="pair-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={12}
            disabled={pairing}
          />
        </p>
        <p>
          <label htmlFor="pair-name">Name this display (optional)</label>
          <br />
          <input
            id="pair-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder="Kitchen"
            disabled={pairing}
          />
        </p>
        {state.status === 'failed' ? <p role="alert">{state.message}</p> : null}
        <p>
          <button type="submit" disabled={pairing || !code.trim()}>
            {pairing ? 'Pairing…' : 'Pair this display'}
          </button>
        </p>
      </form>
    </main>
  );
}
